import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

import translations from './translations.js';

/**
 * Menu for new member invitation
 *
 * @prop invitationId - Id of the invitation
 * @prop resourceName - Invited person email or name
 *
 * @slot trigger - For menu trigger button
 */

export default class MemberInvitationMenu extends BaseMenu {
  static properties = {
    invitationId: { type: Number },
    resourceName: { type: String },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return this.invitationId;
  }

  /** @internal */
  get baseUrl() {
    return `/friend_invitations/${this.invitationId}`;
  }

  /** @internal */
  get resendUrl() {
    return `${this.baseUrl}/resend`;
  }

  /** @internal */
  get resendItem() {
    return {
      title: this.t('actions.resend'),
      action: this.actionFor('resend'),
      icon: 'user-plus',
      partName: 'resend',
    };
  }

  /** @internal */
  get withdrawItem() {
    return {
      title: this.t('actions.withdraw'),
      action: this.actionFor('withdraw'),
      role: 'destructive',
      icon: 'close',
      partName: 'withdraw destructive',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get items() {
    return [this.resendItem, this.divider, this.withdrawItem];
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.resourceName,
      showCancel: true,
    };
  }

  actionFor(method) {
    return {
      menu: 'xs-member-invitation-menu',
      id: this.instanceId,
      method,
    };
  }

  async resend() {
    try {
      await this.commonActions.get(this.resendUrl);
      this.commonActions.displaySnackBarSuccess(
        this.t('snack.resend_success', {
          recipient: this.resourceName,
        })
      );
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  async withdraw() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) return;

    try {
      await this.commonActions.delete(this.baseUrl);
      this.commonActions.displaySnackBarSuccess(
        this.t('snack.withdraw_success')
      );
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <slot name="trigger" slot="trigger">
          <button class="xs-btn btn-naked-dark" aria-label="toggle button">
            <ef-icon name="ellipsis"></ef-icon>
          </button>
        </slot>
      </ef-dot-menu>
    `;
  }
}

MemberInvitationMenu.registerTagName('xs-member-invitation-menu');
MemberInvitationMenu.registerCommonActions('xs-member-invitation-menu');

customElements.get('xs-member-invitation-menu') ||
  customElements.define('xs-member-invitation-menu', MemberInvitationMenu);
